import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { MediaContent } from '@goldcast/api/content';
import { showSuccessToast } from '@/libs/toast/toast';
import { PUSHER_STATES } from '@/context/PusherContext/PusherContextConstants';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { core } from '@/stores/core';
import { isScreenShareDetectionFeatureFlagEnabled, isSimplifiedUploadEnabled } from '@/stores/clip';

const TERMINAL_CLIP_STATUSES = new Set(['DONE', 'FAILED', 'LIMIT_REACHED']);
const TERMINAL_SCREENSHARE_STATES = new Set([
  PUSHER_STATES.SCREENSHARE_DETECTION_DONE,
  PUSHER_STATES.SCREENSHARE_DETECTION_FAILED,
  PUSHER_STATES.SCREENSHARE_DETECTION_UNAVAILABLE
]);
const TERMINAL_SCREENSHARE_API_VALUES = new Set(['DONE', 'FAILED', 'UNAVAILABLE', 'NOT_APPLICABLE']);
const TERMINAL_FACIAL_REC_API_VALUES = new Set(['DONE', 'FAILED', 'UNAVAILABLE', 'NOT_APPLICABLE']);
const TERMINAL_FACIAL_REC_STATES = new Set([
  PUSHER_STATES.FACIAL_RECOGNITION_FULLVIDEO_DONE,
  PUSHER_STATES.FACIAL_RECOGNITION_FULLVIDEO_FAILED
]);

export function isClipsTerminalFromContent(content: MediaContent | null): boolean {
  return TERMINAL_CLIP_STATUSES.has(content?.clip_generation_status ?? '');
}

export function isScreenshareTerminalFromContent(content: MediaContent | null): boolean {
  if (content === null) {
    return false;
  }
  const status = content?.screenshare_detection_status ?? null;
  return status === null || TERMINAL_SCREENSHARE_API_VALUES.has(status as string);
}

export function isFacialRecTerminalFromContent(content: MediaContent | null): boolean {
  if (content === null) {
    return false;
  }
  const status = content?.face_recognition_status ?? null;
  return status === null || TERMINAL_FACIAL_REC_API_VALUES.has(status as string);
}

export interface UploadEditorReadinessState {
  clipsReady: boolean;
  facialRecTerminal: boolean;
  screenshareTerminal: boolean;
  layoutsReady: boolean;
}

export function useUploadEditorReadiness(contentId: string | undefined): UploadEditorReadinessState {
  const isSimplifiedUpload = isSimplifiedUploadEnabled();
  const isScreenshareEnabled = isScreenShareDetectionFeatureFlagEnabled();

  const initialContent = isSimplifiedUpload ? (core.getSnapshot().content as MediaContent | null) : null;
  const initialClipsTerminal = isClipsTerminalFromContent(initialContent);
  const initialFacialRecTerminal = isFacialRecTerminalFromContent(initialContent);
  // When screenshare detection FF is off, treat screenshare as always terminal so it never blocks
  // layouts or the banner — only facial rec gates those surfaces in that case.
  const initialScreenshareTerminal = !isScreenshareEnabled || isScreenshareTerminalFromContent(initialContent);

  const [clipsReady, setClipsReady] = useState(() => !isSimplifiedUpload || initialClipsTerminal);
  const [facialRecTerminal, setFacialRecTerminal] = useState(() => !isSimplifiedUpload || initialFacialRecTerminal);
  const [screenshareTerminal, setScreenshareTerminal] = useState(
    () => !isSimplifiedUpload || initialScreenshareTerminal
  );

  // Track whether layouts were already ready at mount so we only toast on live transitions.
  const layoutsToastShown = useRef(initialFacialRecTerminal && initialScreenshareTerminal);
  const clipsToastShown = useRef(initialClipsTerminal);

  // Reset all state when contentId changes (user navigates to a different session without
  // unmounting the component). Re-reads the store snapshot so the new session's current
  // processing status is reflected immediately.
  useEffect(() => {
    if (!isSimplifiedUpload || !contentId) {
      return;
    }
    const content = core.getSnapshot().content as MediaContent | null;
    const nextClips = isClipsTerminalFromContent(content);
    const nextFacial = isFacialRecTerminalFromContent(content);
    const nextScreenshare = !isScreenshareEnabled || isScreenshareTerminalFromContent(content);
    setClipsReady(nextClips);
    setFacialRecTerminal(nextFacial);
    setScreenshareTerminal(nextScreenshare);
    clipsToastShown.current = nextClips;
    layoutsToastShown.current = nextFacial && nextScreenshare;
  }, [isSimplifiedUpload, contentId, isScreenshareEnabled]);

  // Recover readiness from the API-sourced content. The content often lands in the `core` store AFTER
  // this hook mounts (the layout renders before the detail page's fetch resolves), and for content that
  // was already processed no WS event will follow — without this, the clips block would hang on
  // "Curating AI Clips" forever even though the API already reports clip_generation_status: DONE.
  // Subscribing to `core` makes the hook react to that late update; upgrades are monotonic (false -> true)
  // so they compose with the WS handlers, and the toast refs are marked so this initial reveal is silent.
  const coreContent = useSyncExternalStore(core.subscribe, core.getSnapshot).content as MediaContent | null;
  useEffect(() => {
    if (!isSimplifiedUpload) {
      return;
    }
    if (isClipsTerminalFromContent(coreContent)) {
      clipsToastShown.current = true;
      setClipsReady(true);
    }
    const facialTerminal = isFacialRecTerminalFromContent(coreContent);
    const screenshareTerminalFromApi = !isScreenshareEnabled || isScreenshareTerminalFromContent(coreContent);
    if (facialTerminal) {
      setFacialRecTerminal(true);
    }
    if (screenshareTerminalFromApi) {
      setScreenshareTerminal(true);
    }
    if (facialTerminal && screenshareTerminalFromApi) {
      layoutsToastShown.current = true;
    }
    // Depend on the individual status fields, not the whole `coreContent` object: the store hands out a
    // new object reference on any unrelated core update, which would re-run this effect needlessly.
  }, [
    isSimplifiedUpload,
    coreContent?.clip_generation_status,
    coreContent?.face_recognition_status,
    coreContent?.screenshare_detection_status
  ]);

  useEffect(() => {
    if (!isSimplifiedUpload || !contentId) {
      return;
    }

    const handleClipGeneration = (data: { state: string; upload_id?: string }) => {
      if (data.state !== PUSHER_STATES.CLIP_GENERATION_DONE) {
        return;
      }
      if (data.upload_id && data.upload_id !== contentId) {
        return;
      }
      if (!clipsToastShown.current) {
        clipsToastShown.current = true;
        showSuccessToast('AI clips are ready!');
      }
      setClipsReady(true);
    };

    const handleFacialRec = (data: { state: string; upload_id?: string; broadcast_id?: string }) => {
      if (!TERMINAL_FACIAL_REC_STATES.has(data.state as PUSHER_STATES)) {
        return;
      }
      const matchId = data.upload_id ?? data.broadcast_id;
      if (matchId && matchId !== contentId) {
        return;
      }
      setFacialRecTerminal(true);
    };

    const handleScreenshare = (data: { state: string; upload_id?: string; broadcast_id?: string }) => {
      if (!isScreenshareEnabled) {
        return;
      }
      if (!TERMINAL_SCREENSHARE_STATES.has(data.state as PUSHER_STATES)) {
        return;
      }
      const matchId = data.upload_id ?? data.broadcast_id;
      if (matchId && matchId !== contentId) {
        return;
      }
      setScreenshareTerminal(true);
    };

    const clipListener = EventBus.on(CustomEvents.ClipGenerationStatusUpdated, handleClipGeneration);
    const facialListener = EventBus.on(CustomEvents.FacialRecognitionStatusUpdated, handleFacialRec);
    const screenshareListener = EventBus.on(CustomEvents.ScreenshareDetectionStatusUpdated, handleScreenshare);

    return () => {
      EventBus.off(CustomEvents.ClipGenerationStatusUpdated, clipListener);
      EventBus.off(CustomEvents.FacialRecognitionStatusUpdated, facialListener);
      EventBus.off(CustomEvents.ScreenshareDetectionStatusUpdated, screenshareListener);
    };
  }, [isSimplifiedUpload, contentId, isScreenshareEnabled]);

  // Show layouts-ready toast once both facial rec and screenshare become terminal (only on live transition).
  useEffect(() => {
    if (!isSimplifiedUpload || layoutsToastShown.current) {
      return;
    }
    if (facialRecTerminal && screenshareTerminal) {
      layoutsToastShown.current = true;
      showSuccessToast('Speaker layouts are now available!');
    }
  }, [isSimplifiedUpload, facialRecTerminal, screenshareTerminal]);

  // The AI editor gates on layouts (facial recognition + screenshare terminal) only — it does not
  // depend on AI clip generation — so consumers use layoutsReady directly.
  const layoutsReady = facialRecTerminal && screenshareTerminal;

  return { clipsReady, facialRecTerminal, screenshareTerminal, layoutsReady };
}
